/* eslint-disable react-hooks/exhaustive-deps */
import {Button, Flashbar, Header, SelectProps} from '@amzn/awsui-components-react';
import ColumnLayout from '@amzn/awsui-components-react/polaris/column-layout';
import DatePicker from '@amzn/awsui-components-react/polaris/date-picker';
import ExpandableSection from '@amzn/awsui-components-react/polaris/expandable-section';
import FormField from '@amzn/awsui-components-react/polaris/form-field';
import Input from '@amzn/awsui-components-react/polaris/input';
import Select from '@amzn/awsui-components-react/polaris/select';
import Toggle from '@amzn/awsui-components-react/polaris/toggle';
import React, {ReactNode, useContext, useEffect, useState} from 'react';
import {InputQuery, OutputQuery, WorkflowExecutionStatus, WorkflowExecutionStatusLabel} from '../../backend/model';
import {isValidExecutionId} from '../../validation/InputValidator';
import {getWorkflows} from './workflows';
import {isEqual} from 'lodash';
import {parseErrorMessage} from "../../utils/errors";
import {DropdownStatusProps} from "@amzn/awsui-components-react/polaris/internal/components/dropdown-status/interfaces";
import {
    ADVANCED_SEARCH_TAB_ID,
    INPUT_FIELD_PREFIX,
    OUTPUT_FIELD_PREFIX,
} from '../metadata/Constants';
import {
    convertToPropertyFilterQuery, convertToQuery,
    getPropertyFiltersFromOpenSearchKeys,
} from '../search/SearchUtils';
import GlobalDataContext from '../../context/GlobalDataProvider';
import AdvancedSearch from '../search/AdvancedSearch';
import * as queryString from 'query-string';
import {useHistory} from 'react-router-dom';
import {
    SEARCH_BY_INPUT_I18N_STRINGS,
    SEARCH_BY_OUTPUT_I18N_STRINGS,
} from '../metadata/MetadataSearchProperty';

interface WorkflowNameOption extends SelectProps.Option {
    workflowName: string | undefined;
}

interface WorkflowStatusOption extends SelectProps.Option {
    status: WorkflowExecutionStatus | undefined;
}

const workflowNameOptionAll: WorkflowNameOption = {label: 'All', workflowName: undefined}

const workflowStatusOptions: WorkflowStatusOption[] = [
    {label: 'All', status: undefined},
    {label: WorkflowExecutionStatusLabel.NOT_STARTED, status: WorkflowExecutionStatus.NOT_STARTED},
    {label: WorkflowExecutionStatusLabel.RUNNING, status: WorkflowExecutionStatus.RUNNING},
    {label: WorkflowExecutionStatusLabel.SUCCEEDED, status: WorkflowExecutionStatus.SUCCEEDED},
    {label: WorkflowExecutionStatusLabel.FAILED, status: WorkflowExecutionStatus.FAILED},
]
// TODO: Once we start ingesting in_progress and not_started workflows, we can enable search for all statuses.
const advancedWorkflowStatusOptions: WorkflowStatusOption[] = [
    {label: WorkflowExecutionStatusLabel.SUCCEEDED, status: WorkflowExecutionStatus.SUCCEEDED},
    {label: WorkflowExecutionStatusLabel.FAILED, status: WorkflowExecutionStatus.FAILED},
]

export interface WorkflowHistorySearchPanelProps {
    executionId?: string
    workflowName?: string
    requester?: string
    status: WorkflowExecutionStatus
    fromStartDate?: string
    toStartDate?: string
    inputSearchQuery?: InputQuery
    outputSearchQuery?: OutputQuery
    searchView?: string
    searchHandler: Function
}

const WorkflowHistorySearchPanel: React.FC<WorkflowHistorySearchPanelProps> = (props) => {
    const [workflows, setWorkflows] = useState<WorkflowNameOption[]>()
    const [workflowsLoading, setWorkflowsLoading] = useState<DropdownStatusProps.StatusType>('loading')
    const [executionId, setExecutionId] = useState(props.executionId)
    const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowNameOption>(workflowNameOptionAll)
    const [requester, setRequester] = useState(props.requester)
    const [selectedStatus, setSelectedStatus] = useState<WorkflowStatusOption>(workflowStatusOptions[0])
    const [fromStartDate, setFromStartDate] = useState(props.fromStartDate)
    const [toStartDate, setToStartDate] = useState(props.toStartDate)
    const [apiErrorMessage, setApiErrorMessage] = useState<ReactNode>('')
    const [workflowLoadError, setWorkflowLoadError] = useState<boolean>(false)
    const [inputQuery, setInputQuery] = useState(props.inputSearchQuery)
    const [outputQuery, setOutputQuery] = useState(props.outputSearchQuery)
    const [searchView, setSearchView] = useState(props.searchView)
    const {inputSearchKeys, setInputSearchKeys} = useContext(GlobalDataContext)
    const {outputSearchKeys, setOutputSearchKeys} = useContext(GlobalDataContext)
    const history = useHistory()

    useEffect(() => {
        setExecutionId(props.executionId)
        setWorkflowSelect(props.workflowName)
        setRequester(props.requester)
        setStatusSelect(props.status)
        setFromStartDate(props.fromStartDate)
        setToStartDate(props.toStartDate)
        setSearchView(props.searchView)
    }, [props])

    useEffect(() => {
        resetState()

        setWorkflowLoadError(false)
        getWorkflows(true).then(data => {
            const workflowNameOptions: WorkflowNameOption[] = ([
                workflowNameOptionAll,
            ] as WorkflowNameOption[]).concat(data.map(workflow => ({
                label: workflow.label,
                workflowName: workflow.value,
            })))
            const workflows = data.filter(workflow =>
                selectedWorkflow == workflowNameOptionAll || workflow.value == selectedWorkflow.workflowName
            )
            setInputSearchKeys(
                workflows.flatMap(workflows => workflows.inputs).filter(inputSchema =>
                    inputSchema.parameterIdentifier != null
                ).map(inputSchema => {
                    return {
                        key: INPUT_FIELD_PREFIX + inputSchema.parameterIdentifier,
                        type: inputSchema.type
                    }
                })
            )
            setOutputSearchKeys(
                workflows.flatMap(workflows => workflows.outputs).filter(outputSchema =>
                    outputSchema.parameterIdentifier != null
                ).map(outputSchema => {
                    return {
                        key: OUTPUT_FIELD_PREFIX + outputSchema.parameterIdentifier,
                        type: outputSchema.type
                    }
                })
            )
            setWorkflows(workflowNameOptions)
            setWorkflowsLoading('finished')
        }).catch(error => {
            setWorkflowLoadError(true)
            console.log(error)
            setApiErrorMessage('Error: ' + [parseErrorMessage(error)] + '. No workflows found to search.')
            setWorkflowsLoading('error')
        })
    }, [selectedWorkflow])

    const resetState = (): void => {
        setApiErrorMessage('')
    }

    function executeSearchOnEnter(key: string): void {
        if (key === 'Enter') {
            props.searchHandler(
                executionId,
                selectedWorkflow.workflowName,
                selectedStatus.status,
                requester,
                fromStartDate,
                toStartDate,
                inputQuery,
                outputQuery,
                searchView
            )
        }
    }

    function setWorkflowSelect(workflowName?: string): void {
        if (workflows != null) {
            for (const workflow of workflows) {
                if (workflow.workflowName === workflowName) {
                    setSelectedWorkflow(workflow)
                    return
                }
            }
        }

        setSelectedWorkflow(workflowNameOptionAll)
    }

    function setStatusSelect(status: string): void {
        for (const workflowStatus of workflowStatusOptions) {
            if (workflowStatus.status === status) {
                setSelectedStatus(workflowStatus)
                return
            }
        }

        setSelectedStatus(workflowStatusOptions[0])
    }

    const ExecutionsDisabled = !!executionId
    const ExecutionIdDisabled = !isEqual(selectedWorkflow, workflowNameOptionAll) || !!requester ||
        selectedStatus !== workflowStatusOptions[0] || !!fromStartDate || !!toStartDate

    const [inputSearchFilterProperties, inputSearchFilterOptions] = getPropertyFiltersFromOpenSearchKeys(inputSearchKeys)
    const [outputSearchFilterProperties, outputSearchFilterOptions] = getPropertyFiltersFromOpenSearchKeys(outputSearchKeys)
    const displayedInputQuery = convertToPropertyFilterQuery(inputQuery, INPUT_FIELD_PREFIX)
    const displayedOutputQuery = convertToPropertyFilterQuery(outputQuery, OUTPUT_FIELD_PREFIX)

    const handleSearchToggle = (searchView?: string) => {
        const newView = searchView === ADVANCED_SEARCH_TAB_ID ? undefined : ADVANCED_SEARCH_TAB_ID
        setInputQuery(undefined)
        setOutputQuery(undefined)
        setSearchView(newView)
    }

    const formBody = (
        <ColumnLayout>
            <ColumnLayout columns={3}>
                <FormField id="workflowHistory-executionId" label='Execution ID (optional)'>
                    <Input
                        autoFocus
                        invalid={!!executionId && executionId.length > 0 && !isValidExecutionId(executionId)}
                        value={executionId || ''}
                        onChange={({detail}) => setExecutionId(detail.value.trim())}
                        onKeyDown={({detail}) => executeSearchOnEnter(detail.key)}
                        disabled={ExecutionIdDisabled}
                    />
                </FormField>
                <FormField id="workflowHistory-workflowName" label='Workflow Name (optional)'>
                    <Select
                        options={workflows}
                        selectedOption={selectedWorkflow}
                        onChange={({detail}) => {
                            setSelectedWorkflow(detail.selectedOption as WorkflowNameOption)
                        }}
                        disabled={ExecutionsDisabled}
                        statusType={workflowsLoading}
                        errorText={"Failed to fetch workflows"}
                        loadingText={"Fetching workflows"}
                    />
                </FormField>
                <FormField id="workflowHistory-fromStartDate" label='From Start Date UTC (optional)'>
                    <DatePicker
                        placeholder="YYYY/MM/DD"
                        value={fromStartDate || ''}
                        onChange={({detail}) => setFromStartDate(detail.value)}
                        disabled={ExecutionsDisabled}
                        previousMonthAriaLabel="Previous month"
                        todayAriaLabel="Today"
                        nextMonthAriaLabel="Next month"
                    />
                </FormField>
                <FormField id="workflowHistory-status" label='Status (optional)'>
                    <Select
                        options={searchView == ADVANCED_SEARCH_TAB_ID ? advancedWorkflowStatusOptions : workflowStatusOptions}
                        selectedOption={selectedStatus}
                        onChange={({detail}) => {
                            setSelectedStatus(detail.selectedOption as WorkflowStatusOption)
                        }}
                        disabled={ExecutionsDisabled}
                    />
                </FormField>
                <FormField id="workflowHistory-requester" label='Requester (optional)'>
                    <Input
                        autoFocus
                        value={requester || ''}
                        onChange={({detail}) => setRequester(detail.value.trim())}
                        onKeyDown={({detail}) => executeSearchOnEnter(detail.key)}
                        disabled={ExecutionsDisabled}
                    />
                </FormField>
                <FormField id="workflowHistory-toStartDate" label='To Start Date UTC (optional)'>
                    <DatePicker
                        placeholder="YYYY/MM/DD"
                        value={toStartDate || ''}
                        onChange={({detail}) => setToStartDate(detail.value)}
                        disabled={ExecutionsDisabled}
                        previousMonthAriaLabel="Previous month"
                        todayAriaLabel="Today"
                        nextMonthAriaLabel="Next month"
                    />
                </FormField>
            </ColumnLayout>
            <Toggle id="workflowHistory-advancedSearchToggle"
                    checked={searchView === ADVANCED_SEARCH_TAB_ID}
                    onChange={() => {handleSearchToggle(searchView)}}
            >
                Advanced Search
            </Toggle>
            <ColumnLayout columns={1}>
                { searchView === ADVANCED_SEARCH_TAB_ID &&
                    <>
                        <AdvancedSearch
                            label={"Inputs"}
                            filteringProperties={inputSearchFilterProperties}
                            filteringOptions={inputSearchFilterOptions}
                            displayedQuery={displayedInputQuery}
                            searchHandler={() => {}}
                            onChangeHandler={(query) => {setInputQuery(convertToQuery(query, INPUT_FIELD_PREFIX))}}
                            displaySearchButton={false}
                            i18nStrings={SEARCH_BY_INPUT_I18N_STRINGS}
                        />
                        <AdvancedSearch
                            label={"Outputs"}
                            filteringProperties={outputSearchFilterProperties}
                            filteringOptions={outputSearchFilterOptions}
                            displayedQuery={displayedOutputQuery}
                            searchHandler={() => {}}
                            onChangeHandler={(query) => {setOutputQuery(convertToQuery(query, OUTPUT_FIELD_PREFIX))}}
                            displaySearchButton={false}
                            i18nStrings={SEARCH_BY_OUTPUT_I18N_STRINGS}
                        />
                    </> }
                <Button
                    variant="primary"
                    onClick={
                        () => props.searchHandler(
                            executionId,
                            selectedWorkflow.workflowName,
                            selectedStatus.status,
                            requester,
                            fromStartDate,
                            toStartDate,
                            inputQuery,
                            outputQuery,
                            searchView,
                        )
                    }
                    disabled={workflowLoadError}
                >
                    Search
                </Button>
            </ColumnLayout>
        </ColumnLayout>
    )

    return (
        <>
            <ExpandableSection
                id='workflowHistorySearchPanel'
                variant='container'
                header={<Header variant='h2'>Search</Header>}
                defaultExpanded>
                {formBody}
            </ExpandableSection>
            <br/>
            {
                apiErrorMessage &&
                <Flashbar
                    id='startWorkflowPage-flashbar-error'
                    items={[
                        {
                            type: 'error',
                            content: apiErrorMessage,
                            dismissible: true,
                            onDismiss: () => setApiErrorMessage(''),
                        },
                    ]}
                />
            }
        </>
    )
}

export default WorkflowHistorySearchPanel
